import { useEffect } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  McpAccess,
  OrganizationApplicationConfigurationMaxSessionDuration,
  OrganizationApplicationConfigurationUserSessionInactivityTimeout,
} from '@/types/schema';

import { useOrganizationConfigurationFormQuery } from '../graphql/OrganizationConfigurationForm.generated';
import { OrganizationConfigurationFormShape } from '../OrganizationConfigurationForm.constants';

export function useOrganizationConfigurationFormDefaultValues(
  luminaryOrganizationID: string,
  reset: UseFormReset<OrganizationConfigurationFormShape>
) {
  const { createErrorFeedback } = useFeedback();

  const { data, ...queryProps } = useOrganizationConfigurationFormQuery({
    variables: { luminaryOrganizationID },
    onError: createErrorFeedback(
      'There was an error fetching organization configuration details. Please refresh the page to try again.'
    ),
  });

  useEffect(() => {
    if (!data) return;
    const config = data.organizationApplicationConfigurations.edges?.[0]?.node;
    const formData: OrganizationConfigurationFormShape = {
      collaboratorDomainAllowList: (
        config?.collaboratorDomainAllowList ?? []
      ).map((domain) => ({ value: domain })),
      disableAdvancedAnalytics: config?.disableAdvancedAnalytics ?? false,
      disableAiCapabilities: config?.disableAiCapabilities ?? false,
      disableAiInsights: config?.disableAiInsights ?? false,
      disableRoleManagement: config?.disableRoleManagement ?? false,
      disclaimerConfigurationOverridesDescendants:
        config?.disclaimerConfigurationOverridesDescendants ?? false,
      enableEmployeeIdpId: config?.enableEmployeeIdpID ?? false,
      mcpAccess: config?.mcpAccess ?? McpAccess.Off,
      smartReviewEnabled: config?.smartReviewEnabled ?? true,
      maxSessionDuration:
        config?.maxSessionDuration ??
        OrganizationApplicationConfigurationMaxSessionDuration.TwentyEightDays,
      userSessionInactivityTimeout:
        config?.userSessionInactivityTimeout ??
        OrganizationApplicationConfigurationUserSessionInactivityTimeout.Off,
    };
    reset(formData);
  }, [data, reset]);

  return {
    data,
    ...queryProps,
  };
}
